import { css } from '@emotion/react';
import { UsersApi } from '@frontend/api-users';
import { authnClient } from '@frontend/auth';
import { localStorageHelper, setLoginData, AuthStorage } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { useMutation } from '@frontend/react-query-helpers';
import { UsersTypes } from '@frontend/user-helpers';
import { theme } from '@frontend/theme';
import {
  Alert,
  ContentLoader,
  Heading,
  PrimaryButton,
  Text,
  TextLink,
  WeaveLogoIcon,
  useAlert,
} from '@frontend/design-system';
import { usePublishAccountCreationEvent } from '../hooks/use-publish-account-creation-event';

type Props = {
  token: string;
  locationId: string;
  onSuccess: () => void;
};
export const AcceptInvite = ({ token, locationId, onSuccess }: Props) => {
  const alerts = useAlert();
  const { t } = useTranslation('account');

  usePublishAccountCreationEvent(locationId);

  const { mutate, isSuccess, isLoading, isError } = useMutation({
    mutationFn: (payload: UsersTypes.NonceInvitePayload) => UsersApi.update.locationInvite(payload),
    onSuccess: () => {
      alerts.success(t('Successfully added to location, please sign in to view location.'));
      localStorageHelper.delete(AuthStorage.forward_url);
      /* 
        There is a possibility a user could already be signed in 
        so if they are then we need to sign them out after they have been added to the location. 
       */
      const userAuthenticated = authnClient.isUserAuthenticated();

      //TODO: if the user was previously logged into a different location, this won't override their last-location id.
      // if we want it to, we need to know the userId, so we need to get it somehow (maybe from the nonce?)
      setLoginData('*', { latestLocationId: locationId });
      if (userAuthenticated) {
        authnClient.signOut();
      } else {
        onSuccess();
      }
    },
  });

  return (
    <div css={acceptInviteStyle}>
      <WeaveLogoIcon size={52} css={{ marginTop: theme.spacing(10) }} />
      {isSuccess ? (
        <>
          <Heading css={inviteHeading}>{t("Perfect! You've successfully joined!")}</Heading>
          <Text size='medium'>
            {t('This page will redirect to the login page in a few seconds.')}
            <br />
            {t('Click')} <TextLink to='/sign-in'>{t('Sign In ')}</TextLink>
            {t('to immediately go to the login page.')}
          </Text>
        </>
      ) : (
        <>
          <Heading css={inviteHeading}>
            {t("You've been invited to join")} <br /> {t("Weave's Admin Portal!")}
          </Heading>

          <Text size='medium'>
            {t("Accepting your invitation will give you access to the Weave's admin portal app.")}
            <br />
            {t(
              'We use this app to configure office settings and perform various admin tasks (like Data Import Tool - prev. CSV Upload).'
            )}
          </Text>
          <ContentLoader show={isLoading} message={t('accepting invitation')} />
          <PrimaryButton
            onClick={() => mutate({ nonceToken: token, locationId })}
            css={css`
              max-width: 200px;
              margin: ${theme.spacing(3, 0)};
            `}
            disabled={isLoading}
          >
            {t('Accept Invitation')}
          </PrimaryButton>
          {isError && (
            <Alert type='error'>
              <Text as='span' size='medium' textAlign='left'>
                {t(
                  'There was an error when trying to validate your user. Validation tokens are only good for 7 days. '
                )}
                <strong>{t('Please, talk to your administrator, generate a new token and try again.')}</strong>
              </Text>
            </Alert>
          )}
        </>
      )}
    </div>
  );
};

const acceptInviteStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin: 0 auto;
`;

const inviteHeading = css`
  font-size: ${theme.fontSize(36)};
  text-align: center;
  margin: ${theme.spacing(3, 0)};
`;
